import { forwardRef, type ReactNode } from 'react';
import cn from 'clsx';
import Minus from '@pkgs/components/assets/icons/xsmall/Minus.svg';
import Check from '@pkgs/components/assets/icons/xsmall/Check.svg';
import { Copy, type CopyProps } from '@ev-tech/ui-core-components/Copy';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';

import styles from './Checkbox.module.css';

export type CheckboxProps = {
  className?: string;
  label?: ReactNode;
  size?: CopyProps['size'];
  'data-testid'?: string;
  'aria-label'?: string;
  isSelected?: boolean;
  defaultSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  name?: string;
  value?: string;
  id?: string;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      isDisabled,
      isIndeterminate = false,
      isSelected,
      defaultSelected,
      onChange,
      label,
      size,
      name,
      value,
      id,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
    },
    forwardedRef,
  ) => {
    return (
      // Reason: On <label> (not Root) so Base UI aria-labelledby uses the short name when it differs from visible text (e.g. "Consent to marketing").
      <label className={cn(styles.container, className)} aria-label={ariaLabel}>
        <BaseCheckbox.Root
          id={id}
          name={name}
          value={value}
          checked={isSelected}
          defaultChecked={defaultSelected}
          disabled={isDisabled}
          indeterminate={isIndeterminate}
          onCheckedChange={(checked) => onChange?.(checked)}
          inputRef={forwardedRef}
          className={styles.checkbox}
          data-testid={dataTestId}
        >
          <BaseCheckbox.Indicator className={styles.indicator} keepMounted={isIndeterminate}>
            {isIndeterminate ? <Minus className={styles.icon} /> : <Check className={styles.icon} />}
          </BaseCheckbox.Indicator>
        </BaseCheckbox.Root>
        {label ? <Copy size={size} as="span" text={label} /> : null}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
